import React, { useCallback } from "react";
import { Platform, Share, View } from "react-native";
import QRCode from "react-qr-code";
import tw from "twrnc";

import useClipboardCallback from "@/components/hooks/useClipboardCallback";
import Text from "@/components/shared/AppText";
import Button from "@/components/settings/Button";
import { SectionTitle } from "@/components/settings/SettingsPage";
import { colors, radius, text } from "@/components/settings/theme";
import { t } from "@/localization";
import { roomLabelOf } from "./rooms";

// The link that streams one camera from any phone: it opens the EventLive app
// (through the store the first time) ready to stream this camera's key, with
// no sign-in. It's the "Delegate going live" link, one per camera.
export const cameraLinkOf = (event) =>
  `https://eventlive.app.link/streamFor?streamKey=${encodeURIComponent(
    event?.stream_key || ""
  )}&$ios_nativelink=true`;

// How to Go Live for a camera: hand it to another phone with a code to scan
// or a link to send.
const CameraLinkCard = ({ event, style }) => {
  const label = roomLabelOf(event);
  const link = cameraLinkOf(event);
  const copy = useClipboardCallback();
  // Sent to the friend who films: in the app's language, like any message the
  // user sends.
  const eventTitle = event.title ? event.title.replace(/\s+—\s+.*$/, "") : null;
  const message = `${
    eventTitle === null
      ? t("You're filming {label} at the event.", { label })
      : t("You're filming {label} at {event}.", { label, event: eventTitle })
  } ${t(
    "Install EventLive on your phone, then open this link on the day to start streaming: {link}",
    { link }
  )}\n\n${t(
    "Use a tripod, keep the phone charging and stay close to the action."
  )}`;

  const send = useCallback(async () => {
    if (Platform.OS === "web") {
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({
            title: t("Stream {label}", { label }),
            text: message,
          });
          return;
        } catch (e) {}
      }
      copy(message);
      return;
    }
    try {
      await Share.share({ message, url: link });
    } catch (e) {
      copy(message);
    }
  }, [copy, label, link, message]);

  return (
    <View
      style={[
        tw`p-4`,
        { backgroundColor: colors.surface, borderRadius: radius.card },
        style,
      ]}
    >
      <SectionTitle
        title={t("Stream {label} from another phone", { label })}
        description={t(
          "Point the other phone's camera at this code: EventLive opens ready to stream this camera. No account or password needed."
        )}
      />
      <View style={tw`flex-row items-start`}>
        <View style={tw`bg-white rounded-lg p-2 border border-gray-200`}>
          <QRCode value={link} size={120} />
        </View>
        <View style={tw`flex-1 ml-4`}>
          <Button
            text={t("Send the link")}
            icon="share-outline"
            size="md"
            onPress={send}
          />
          <Text style={[text.hint, tw`mt-2`]}>
            {t(
              "Send it to the friend who'll hold or set up this phone. It opens the app, or the store first if they don't have it."
            )}
          </Text>
        </View>
      </View>
    </View>
  );
};

export default CameraLinkCard;
